import React, { useId, useState } from 'react'
import toast from 'react-hot-toast'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { complianceAPI } from '../../services/api'
import type { CreatorVettingResponse } from '../../types'
import useEscapeKey from '../../hooks/useEscapeKey'
import Spinner from '../Spinner'

// Where the creator is based. Stored as a short code (the column is 8 chars).
const JURISDICTIONS: { code: string; label: string }[] = [
  { code: 'US', label: 'United States' },
  { code: 'CA', label: 'Canada' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'EU', label: 'European Union' },
  { code: 'AU', label: 'Australia' },
  { code: 'OTHER', label: 'Somewhere else' },
]

const input =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500'
const help = 'text-xs text-gray-500 mt-1'

interface VettingApplicationFormProps {
  /** After a rejection: the button says "Resubmit application". */
  resubmit?: boolean
  onSubmitted: (result: CreatorVettingResponse) => void
  onCancel?: () => void
}

/**
 * The creator-vetting application (POST /api/compliance/creator-vetting):
 * a self-attestation our team reviews before monetization features open.
 * Used on Monetization (in VettingApplicationModal) and in Settings.
 */
const VettingApplicationForm: React.FC<VettingApplicationFormProps> = ({
  resubmit = false,
  onSubmitted,
  onCancel,
}) => {
  const id = useId()
  const [legalName, setLegalName] = useState('')
  const [jurisdiction, setJurisdiction] = useState('US')
  const [isAdviser, setIsAdviser] = useState(false)
  const [registrationDetails, setRegistrationDetails] = useState('')
  const [compensation, setCompensation] = useState('')
  const [attested, setAttested] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fail = (message: string) => {
    setError(message)
    toast.error(message)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!legalName.trim()) return fail('Legal name is required')
    if (isAdviser && !registrationDetails.trim()) {
      return fail('Add your registration details (firm name and CRD number)')
    }
    if (!attested) return fail('You must confirm the attestation to submit')

    setSubmitting(true)
    try {
      const result = await complianceAPI.submitVettingApplication({
        legal_name: legalName.trim(),
        jurisdiction,
        is_registered_advisor: isAdviser,
        attestation_accurate: attested,
        registration_details: (isAdviser && registrationDetails.trim()) || undefined,
        compensation_disclosure: compensation.trim() || undefined,
      })
      toast.success('Application submitted for review')
      onSubmitted(result)
    } catch (err: any) {
      fail(err?.response?.data?.detail || 'Failed to submit application')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
      <p className="text-sm text-gray-600">
        Before you can charge for content, we check who you are and whether you have
        any ties that subscribers should know about. It takes a couple of minutes, and
        our team reviews every application by hand. Free posts and signals don&apos;t
        need it.
      </p>

      {error && (
        <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      <div>
        <label htmlFor={`${id}-name`} className="block text-sm font-medium text-gray-700 mb-1">
          Legal name
        </label>
        <input
          id={`${id}-name`}
          type="text"
          value={legalName}
          onChange={(e) => setLegalName(e.target.value)}
          placeholder="Your full legal name"
          autoComplete="name"
          className={input}
        />
        <p className={help}>
          The name on your ID. Paid content is sold under it, and it must match your
          payout account.
        </p>
      </div>

      <div>
        <label htmlFor={`${id}-jurisdiction`} className="block text-sm font-medium text-gray-700 mb-1">
          Where you&apos;re based
        </label>
        <select
          id={`${id}-jurisdiction`}
          value={jurisdiction}
          onChange={(e) => setJurisdiction(e.target.value)}
          className={input}
        >
          {JURISDICTIONS.map((j) => (
            <option key={j.code} value={j.code}>
              {j.label}
            </option>
          ))}
        </select>
        <p className={help}>
          Rules for paid financial content differ by country. Monetization is available
          to creators in the United States first.
        </p>
      </div>

      <fieldset>
        <legend className="block text-sm font-medium text-gray-700 mb-1">
          Are you a registered investment adviser?
        </legend>
        <p className={`${help} mt-0 mb-2`}>
          Includes broker-dealers and anyone working for one. Registered advisers have
          extra disclosure duties when they charge for content. Saying yes doesn&apos;t
          stop you applying; it tells us what to check.
        </p>
        <div className="flex gap-6">
          {[
            { value: false, label: 'No' },
            { value: true, label: 'Yes' },
          ].map((option) => (
            <label key={option.label} className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="radio"
                name={`${id}-adviser`}
                checked={isAdviser === option.value}
                onChange={() => setIsAdviser(option.value)}
              />
              {option.label}
            </label>
          ))}
        </div>
      </fieldset>

      {isAdviser && (
        <div>
          <label htmlFor={`${id}-registration`} className="block text-sm font-medium text-gray-700 mb-1">
            Registration details
          </label>
          <textarea
            id={`${id}-registration`}
            value={registrationDetails}
            onChange={(e) => setRegistrationDetails(e.target.value)}
            rows={2}
            placeholder="Firm name and CRD number"
            className={input}
          />
          <p className={help}>Our team uses these to check your registration.</p>
        </div>
      )}

      <div>
        <label htmlFor={`${id}-compensation`} className="block text-sm font-medium text-gray-700 mb-1">
          Compensation disclosure <span className="font-normal text-gray-500">(optional)</span>
        </label>
        <textarea
          id={`${id}-compensation`}
          value={compensation}
          onChange={(e) => setCompensation(e.target.value)}
          rows={2}
          placeholder="e.g. sponsorships, affiliate partnerships, paid promotions"
          className={input}
        />
        <p className={help}>
          Anyone who pays you to mention a coin, exchange or product. Subscribers deserve
          to know when a call might not be independent.
        </p>
      </div>

      <div className="flex items-start gap-2">
        <input
          id={`${id}-attest`}
          type="checkbox"
          checked={attested}
          onChange={(e) => setAttested(e.target.checked)}
          className="mt-1"
        />
        <label htmlFor={`${id}-attest`} className="text-sm text-gray-700">
          I attest that the information above is accurate, and I understand my content
          and signals are for informational purposes only and do not constitute
          personalized investment advice.
        </label>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 inline-flex items-center gap-2"
        >
          {submitting && <Spinner size="sm" className="border-white border-t-transparent" />}
          {submitting ? 'Submitting...' : resubmit ? 'Resubmit application' : 'Submit application'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  )
}

interface VettingApplicationModalProps {
  isOpen: boolean
  resubmit?: boolean
  onClose: () => void
  onSubmitted: (result: CreatorVettingResponse) => void
}

/** The application in a dialog, opened from the Monetization page. */
export const VettingApplicationModal: React.FC<VettingApplicationModalProps> = ({
  isOpen,
  resubmit = false,
  onClose,
  onSubmitted,
}) => {
  const titleId = useId()
  useEscapeKey(onClose, isOpen)
  if (!isOpen) return null
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="bg-white rounded-lg max-w-xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 id={titleId} className="text-xl font-bold text-gray-900">
            {resubmit ? 'Resubmit your application' : 'Apply to monetize'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="p-6">
          <VettingApplicationForm resubmit={resubmit} onSubmitted={onSubmitted} onCancel={onClose} />
        </div>
      </div>
    </div>
  )
}

export default VettingApplicationForm
